import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {useAppDispatch} from "../../../../../../redux";
import Api from "../../../../../../../core/services/api/api";
import {ProductInformationVendorsDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import DateUtils from "../../../../../../../core/services/date-utils";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import IconButton from "../../../../../../components/base/button/icon_button";
import {AddOutlined, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import {Box, Button, Typography} from "@mui/material";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import UpsertVendorPartDialog from "../../../../../../components/dialogs/upsert-vendor-part";
import Grid2 from "@mui/material/Unstable_Grid2";
import VendorPartAutocomplete from "../../../../../../components/text-fields/autocomplete/vendor-autocomplete/vendor-part";
import useAccessLevels from "../../../../../../hooks/use-access-levels";

const exportingChunkSize = 100;

const dataGridSaveConfig = {
    name: "ProductInformationVendorsViewDataGrid",
    version: 1,
}
const initUpsertVendorPartDialog = {open: false, record: null};

const ProductInformationVendorsView = ({loading, partNo, partId}) => {
    const [data, setData] = useState([]);
    const [upsertVendorPartDialogState, setUpsertVendorPartDialogState] = useState(initUpsertVendorPartDialog);
    const [removingIds, setRemovingIds] = useState([]);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const operatorAccessLevels = useAccessLevels();
    const canManageProducts = operatorAccessLevels.packages.base.manageVendorProducts;
    const canExportProducts = operatorAccessLevels.packages.base.exportVendorProducts;

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: ProductInformationVendorsDataGridColumnNames.remove,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    pinned: true,
                    width: 0,
                },
                {
                    name: ProductInformationVendorsDataGridColumnNames.edit,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    reorderable: false,
                    pinned: true,
                    width: 0,
                },
                {
                    title: "Vendor Code",
                    name: ProductInformationVendorsDataGridColumnNames.vendorCode,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Vendor Part No",
                    name: ProductInformationVendorsDataGridColumnNames.vendorPartNumber,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Vendor Price",
                    name: ProductInformationVendorsDataGridColumnNames.vendorPrice,
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.money,
                    sortable: false,
                },
                {
                    title: "Start Date",
                    name: ProductInformationVendorsDataGridColumnNames.startDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.dateTime,
                    sortable: false,
                },
                {
                    title: "End Date",
                    name: ProductInformationVendorsDataGridColumnNames.endDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.dateTime,
                    sortable: false,
                },
                {
                    title: "Currently Active",
                    name: ProductInformationVendorsDataGridColumnNames.isCurrentlyActive,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.boolean,
                    sortable: false,
                    width: 140,
                },
                {
                    title: "Submitter",
                    name: ProductInformationVendorsDataGridColumnNames.submitter,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Submit Date",
                    name: ProductInformationVendorsDataGridColumnNames.submitDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.dateTime,
                    sortable: false,
                },
                {
                    title: "Updater",
                    name: ProductInformationVendorsDataGridColumnNames.updater,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                },
                {
                    title: "Update Date",
                    name: ProductInformationVendorsDataGridColumnNames.updateDateTime,
                    alignment: DataGridColumnAlignments.left,
                    type: DataGridColumnTypes.dateTime,
                    sortable: false,
                }
            ].filter((column) => canManageProducts || (
                column.name !== ProductInformationVendorsDataGridColumnNames.remove &&
                column.name !== ProductInformationVendorsDataGridColumnNames.edit
            )),
        [canManageProducts])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])


    /**
     * Gets the list of entries based on the filters from the user.
     * If the function is being called for export, the pagination info is passed as the input
     *
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const response = await Api.listVendorParts({
            pagination: args.paginationInfo,
            vendorIds: args.filters?.vendors?.map(e => e.vendorId) ?? [],
            partIds: [partId],
        }, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.totalCount ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted, partId]);

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Exports the list of records.
     * Calls the function for formatting the data
     * @return {Promise<*>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag)
                return;

            if (!res?.data?.length)
                break;

            entries.push(...res.data);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!entries.length)
            return;
        return {
            title: 'Vendors for Part Number ' + partNo,
            body: entries.map((e, i) => ({
                '': i + 1,
                "Connection/Conflict ID": e.id,
                "Vendor Code": e.vendorCode ?? "",
                "Vendor Part No": e.vendorPartNumber ?? "",
                "Vendor Price": e.vendorPrice ? formatMoney(e.vendorPrice) : "",
                "Start Date": e.startDateTime ? DateUtils.serverToLocal(e.startDateTime) : "",
                "End Date": e.endDateTime ? DateUtils.serverToLocal(e.endDateTime) : "",
                "Currently Active": e.isCurrentlyActive ? 'Yes' : 'No',
                "Submitter": e.submitter ?? "",
                "Submit Date": e.submitDateTime ? DateUtils.serverToLocal(e.submitDateTime) : "",
                "Updater": e.updater ?? "",
                "Update Date": e.updateDateTime ? DateUtils.serverToLocal(e.updateDateTime) : "",
            }))
        }
    }, [searchData, partNo, fetchEntries, isMounted])


    /**
     * Gets the removal confirmation for the property
     * @type {(function(*, number=): void)}
     */
    const getRemoveConfirmation = useCallback((record) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `Vendor Part No: ${record.vendorPartNumber}, Vendor Code: ${record.vendorCode}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setRemovingIds(prevState => [...prevState, record.id]);
                const response = await Api.deleteVendorPart(record.id);
                if (!isMounted())
                    return;
                setRemovingIds(prevState => prevState.filter(e => e !== record.id));
                if (response?.resultFlag) {
                    setData(prevState => prevState.filter(e => e.id !== record.id));
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted])

    /**
     * Gets the function to open the upsert dialog with the record to be edited
     * @type {(function(*): void)|*}
     */
    const openUpsertDialog = useCallback((record) => {
        setUpsertVendorPartDialogState({open: true, record: record})
    }, [])

    /**
     * Sets the data after the upsert dialog is closed with the new/edited record and the list of removed record ids (in case of edit)
     * @type {(function(*, *): void)}
     */
    const onUpsertSuccess = useCallback((upsertedRecord, removedRecordIds) => {
        if (removedRecordIds?.length) {
            searchData.setPaginationInfo(prev => ({...prev, length: Math.max(prev.length - removedRecordIds.length, 0)}))
        }
        setData(prevState => {
            const newState = [...prevState];
            if (removedRecordIds?.length) {
                for (const removedId of removedRecordIds) {
                    const index = newState.findIndex(e => e.id === removedId);
                    if (index !== -1)
                        newState.splice(index, 1);
                }
            }
            const recordIndex = prevState.findIndex(e => e.id === upsertedRecord.id);
            if (recordIndex === -1) {
                return [upsertedRecord, ...newState];
            } else {
                newState[recordIndex] = upsertedRecord;
                return newState;
            }
        })
    }, [searchData])

    /**
     * Data grid rows entities
     * @type {unknown}
     */
    const dataGridRows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [ProductInformationVendorsDataGridColumnNames.remove]:
                    <IconButton
                        color={'paper.error'}
                        disabled={removingIds.includes(item.id)}
                        onPressed={() => getRemoveConfirmation(item)}
                    >
                        <DeleteOutlined className={'icon small'}/>
                    </IconButton>,
                [ProductInformationVendorsDataGridColumnNames.edit]:
                    <IconButton
                        color={'paper.primary'}
                        disabled={removingIds.includes(item.id)}
                        onPressed={() => openUpsertDialog(item)}
                    >
                        <EditOutlined className={'icon small'}/>
                    </IconButton>,
                [ProductInformationVendorsDataGridColumnNames.vendorCode]: item.vendorCode,
                [ProductInformationVendorsDataGridColumnNames.vendorPartNumber]: item.vendorPartNumber,
                [ProductInformationVendorsDataGridColumnNames.vendorPrice]: item.vendorPrice,
                [ProductInformationVendorsDataGridColumnNames.startDateTime]: item.startDateTime,
                [ProductInformationVendorsDataGridColumnNames.endDateTime]: item.endDateTime,
                [ProductInformationVendorsDataGridColumnNames.isCurrentlyActive]: item.isCurrentlyActive ?? false,
                [ProductInformationVendorsDataGridColumnNames.submitter]: item.submitter,
                [ProductInformationVendorsDataGridColumnNames.submitDateTime]: item.submitDateTime,
                [ProductInformationVendorsDataGridColumnNames.updater]: item.updater,
                [ProductInformationVendorsDataGridColumnNames.updateDateTime]: item.updateDateTime,
            }
        }))
    }, [data, getRemoveConfirmation, openUpsertDialog, removingIds])

    return (
        <>
            <div className={'w-100 part-info-paper d-flex flex-column p-3'}>
                {
                    loading &&
                    <>
                        <div className='my-2 w-100'/>
                        <LoadingLottieAnimation className={''} width={'80%'}/>
                        <div className='my-2 w-100'/>
                    </>
                }
                {
                    !loading &&
                    <>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 4, width: '100%'}}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, width: '100%'}}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    Manage Product's Vendors
                                </Typography>
                                {
                                    canManageProducts &&
                                    <Button
                                        variant={'outlined'}
                                        color={'primary'}
                                        startIcon={<AddOutlined/>}
                                        onClick={() => openUpsertDialog()}
                                    >
                                        Add Record
                                    </Button>
                                }
                            </Box>
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12}>
                                    <VendorPartAutocomplete
                                        label={"Filter By Vendors"}
                                        partId={partId}
                                        partNo={partNo}
                                        multiple
                                        value={searchData.filters?.vendors ?? []}
                                        onChange={(e, newValue) => searchData.setFilters(prevState => ({...prevState, vendors: newValue}))}
                                    />
                                </Grid2>
                            </Grid2>
                            <PartnerDataGrid
                                {...dataGridSaveConfig}
                                initialLayoutState={{columns: columns,}}
                                columns={dataGridColumns}
                                rows={dataGridRows}
                                getExportInfo={canExportProducts ? exportToCsv : undefined}
                                state={{
                                    pagination: searchData.paginationInfo,
                                    loading: {state: searchData.loading},
                                    density: dataGridDensity,

                                }}
                            />
                        </Box>
                    </>
                }
            </div>
            <UpsertVendorPartDialog
                partId={partId}
                partNo={partNo}
                close={() => setUpsertVendorPartDialogState(initUpsertVendorPartDialog)}
                onSuccess={onUpsertSuccess}
                {...upsertVendorPartDialogState}
            />
        </>
    );
}

export default ProductInformationVendorsView;